import { createUnifiedTheme } from '@backstage/theme';

/**
 * IMDB-inspired dark theme for MAC Developer Portal.
 *
 * Key design choices:
 * - Near-black backgrounds (#121212, #1a1a1a, #1f1f1f)
 * - Gold/amber accent (#f5c518) — IMDB's signature yellow
 * - White primary text, muted gray secondary text
 * - Compact, dense tables and lists
 * - Clean sans-serif typography
 */
export const imdbDarkTheme = createUnifiedTheme({
  palette: {
    type: 'dark',
    mode: 'dark',
    primary: {
      main: '#f5c518', // IMDB gold
      light: '#ffd74a',
      dark: '#c49b00',
      contrastText: '#000000',
    },
    secondary: {
      main: '#e0e0e0',
      light: '#ffffff',
      dark: '#aeaeae',
      contrastText: '#000000',
    },
    background: {
      default: '#121212',
      paper: '#1a1a1a',
    },
    text: {
      primary: '#f5f5f5',
      secondary: '#999999',
    },
    navigation: {
      background: '#0a0a0a',
      indicator: '#f5c518',
      color: '#cccccc',
      selectedColor: '#f5c518',
      navItem: {
        hoverBackground: '#1f1f1f',
      },
    },
    divider: '#333333',
    error: {
      main: '#cf6679',
    },
    warning: {
      main: '#f5c518',
    },
    info: {
      main: '#5c9ce6',
    },
    success: {
      main: '#4caf50',
    },
    banner: {
      info: '#1a1a2e',
      error: '#3d1c1c',
      text: '#f5f5f5',
      link: '#f5c518',
      closeButtonColor: '#999999',
      warning: '#3d3200',
    },
  } as any,
  defaultPageTheme: 'home',
  fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  components: {
    // --- Global baseline ---
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#121212',
          color: '#f5f5f5',
        },
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: '#1a1a1a',
        },
        '*::-webkit-scrollbar-thumb': {
          background: '#444',
          borderRadius: '4px',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      },
    },

    // --- Paper / Cards ---
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          backgroundImage: 'none',
          borderRadius: '4px',
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.5)',
        },
      },
    },

    // --- Cards ---
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1f1f1f',
          border: '1px solid #2a2a2a',
          borderRadius: '4px',
          transition: 'border-color 0.2s ease',
          '&:hover': {
            borderColor: '#f5c518',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          fontSize: '1rem',
          fontWeight: 600,
        },
      },
    },

    // --- Tables (IMDB credits style) ---
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderBottom: '2px solid #f5c518',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #2a2a2a',
          '&:hover': {
            backgroundColor: '#1f1f1f !important',
          },
          '&:nth-of-type(even)': {
            backgroundColor: '#161616',
          },
        },
        head: {
          backgroundColor: '#0a0a0a',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #2a2a2a',
          padding: '10px 16px',
          fontSize: '0.875rem',
        },
        head: {
          color: '#f5c518',
          fontWeight: 700,
          textTransform: 'uppercase' as const,
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
        },
      },
    },

    // --- Buttons ---
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'none' as const,
          fontWeight: 600,
        },
        contained: {
          backgroundColor: '#f5c518',
          color: '#000000',
          '&:hover': {
            backgroundColor: '#ffd74a',
          },
        },
        outlined: {
          borderColor: '#f5c518',
          color: '#f5c518',
          '&:hover': {
            backgroundColor: 'rgba(245, 197, 24, 0.08)',
            borderColor: '#ffd74a',
          },
        },
        text: {
          color: '#f5c518',
          '&:hover': {
            backgroundColor: 'rgba(245, 197, 24, 0.08)',
          },
        },
      },
    },

    // --- Links ---
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#5c9ce6',
          '&:hover': {
            color: '#f5c518',
          },
        },
      },
    },

    // --- Chips / Tags ---
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#2a2a2a',
          color: '#e0e0e0',
          border: '1px solid #3a3a3a',
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(245, 197, 24, 0.15)',
          color: '#f5c518',
          border: '1px solid rgba(245, 197, 24, 0.3)',
        },
      },
    },

    // --- Tabs ---
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#f5c518',
          height: '3px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#999999',
          textTransform: 'none' as const,
          fontWeight: 600,
          fontSize: '0.875rem',
          '&.Mui-selected': {
            color: '#f5c518',
          },
        },
      },
    },

    // --- Inputs ---
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#1f1f1f',
          color: '#f5f5f5',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3a3a3a',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#666666',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#f5c518',
          },
        },
      },
    },

    // --- Toolbar / AppBar ---
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',
          boxShadow: '0 1px 0 #2a2a2a',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',
        },
      },
    },

    // --- Breadcrumbs ---
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          color: '#999999',
        },
        separator: {
          color: '#666666',
        },
      },
    },

    // --- Accordion ---
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          borderBottom: '1px solid #2a2a2a',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#1f1f1f',
          },
        },
      },
    },

    // --- Dialogs ---
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1a',
          border: '1px solid #333333',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #f5c518',
        },
      },
    },

    // --- Select / Menu ---
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1a',
          border: '1px solid #333333',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: '#1f1f1f',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(245, 197, 24, 0.1)',
          },
        },
      },
    },

    // --- Backstage-specific component overrides ---
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'none',
          backgroundColor: '#0a0a0a',
          borderBottom: '3px solid #f5c518',
          boxShadow: 'none',
          padding: '16px 24px',
        },
        title: {
          color: '#f5f5f5',
          fontWeight: 700,
          fontSize: '1.5rem',
        },
        subtitle: {
          color: '#999999',
        },
      },
    },
    BackstageHeaderLabel: {
      styleOverrides: {
        label: {
          color: '#999999',
          fontSize: '0.75rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.08em',
        },
        value: {
          color: '#f5c518',
          fontWeight: 600,
        },
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        headerTitle: {
          fontWeight: 700,
          fontSize: '1rem',
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1f1f1f',
          borderBottom: '2px solid #f5c518',
        },
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
        },
        title: {
          color: '#f5c518',
          fontWeight: 700,
          textTransform: 'uppercase' as const,
        },
      },
    },
    BackstageTable: {
      styleOverrides: {
        root: {
          '& > div > div > table > thead > tr > th': {
            backgroundColor: '#0a0a0a',
            color: '#f5c518',
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            borderBottom: '2px solid #f5c518',
          },
        },
      },
    },
  },
});
